import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DateTime } from "luxon";
import { IconButton } from "@mui/material";
import { DatePicker, DateView } from "@mui/x-date-pickers-pro";
import ClearIcon from "@mui/icons-material/Clear";

import { formInputsStyles } from "../../Configuration/styles";

/**
 * Reset Button
 *
 * @param onReset
 */
const ResetButton = ({ onReset }: { onReset: () => void }) => {
  return (
    <IconButton
      onClick={() => onReset()}
      aria-label="reset"
      sx={{
        order: 1,
        padding: 0,
        fontSize: "1em",
        opacity: 0,
        color: "text.secondary",
      }}
    >
      <ClearIcon sx={{ fontSize: "1.25em" }} />
    </IconButton>
  );
};

/**
 * Date Picker filter
 *
 * Filter of date to refine the search in table
 *
 * @param label
 * @param onChange
 */
const CustomDatePicker = ({
  label,
  translateLabel = true,
  value,
  maxDate,
  minDate,
  views,
  disabled,
  onChange,
}: {
  label: string;
  translateLabel?: boolean;
  value?: string | null;
  maxDate?: DateTime<boolean> | undefined;
  minDate?: DateTime<boolean> | undefined;
  views?: DateView[];
  disabled?: boolean;
  onChange: (newValue: string | null) => void;
}) => {
  const inputStyles = formInputsStyles();
  const intl = useIntl();
  const [date, setDate] = useState<DateTime | null>(
    value ? DateTime.fromISO(value) : null
  );

  useEffect(() => {
    setDate(value ? DateTime.fromISO(value) : null);
  }, [value]);

  const handleReset = () => {
    setDate(null);
    onChange(null);
  };

  const handleChange = (date: DateTime | null) => {
    setDate(date);
    if (date) {
      onChange(date.toISODate());
    } else {
      onChange(null);
    }
  };

  return (
    <DatePicker
      className={inputStyles.classes.inputText}
      closeOnSelect
      onChange={(date) => handleChange(date)}
      maxDate={maxDate}
      minDate={minDate}
      value={date}
      views={views}
      disabled={disabled}
      slotProps={{
        toolbar: {
          hidden: true,
        },
        textField: {
          slotProps: {
            input: {
              startAdornment: <ResetButton onReset={handleReset} />,
            },
            htmlInput: {
              placeholder: translateLabel
                ? intl.formatMessage({ id: label })
                : label,
            },
          },
          size: "small",
          sx: [
            {
              "&:hover .MuiButtonBase-root.MuiIconButton-root": { opacity: 1 },
              "& .MuiInputAdornment-positionEnd": { height: "0.01em" },
            },
          ],
        },
        inputAdornment: {
          position: "end",
          sx: { order: 2, marginLeft: 0 },
        },
      }}
    />
  );
};

export default CustomDatePicker;
